import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Radio, Settings, Award, Lock, ArrowRight, Users, PartyPopper,
  Monitor, Sliders, Zap, Music2,
} from 'lucide-react'
import { Card, CardContent } from '../components/ui/card'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { useAuth } from '../hooks/useAuth'
import { visionAPI } from '../lib/api'
import KempenvisionNav from '../components/KempenvisionNav'
import CompoStatusStrip from '../components/CompoStatusStrip'

/* Fase-label + eerstvolgende-actie-berekening voor de statusbalk.
   Vervangt de losse "wat is er nu"-cards en zet één regel bovenaan. */
const KV_FASE = {
  setup: 'Setup', live_songs: 'Nummers', jury_reveal: 'Jury reveal',
  public_vote: 'Publiek stemt', public_reveal: 'Publiek reveal', finished: 'Klaar',
}
function kvNextAction({ show, subState, voteState, juryState }) {
  if (!show) return null
  if (show.phase === 'setup')       return subState?.can_submit ? 'Lever je nummer in' : 'Wachten op start'
  if (show.phase === 'live_songs')  return 'Nummers spelen op de show'
  if (show.phase === 'jury_reveal') return juryState?.jury && !juryState.jury.is_locked ? 'Vul jouw jury-punten in' : 'Jury geeft punten'
  if (show.phase === 'public_vote') return voteState?.is_open && !voteState.already_voted ? 'Stem op je favoriet' : 'Publiek stemt'
  if (show.phase === 'public_reveal') return 'Reveal van de uitslag'
  if (show.phase === 'finished')    return 'Show voorbij'
  return null
}
import PageHeader from '../components/PageHeader'
import SubmissionStatusCard from '../components/SubmissionStatusCard'

export default function KempenvisionPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin'
  const [myJury, setMyJury] = useState(null)
  const [voteState, setVoteState] = useState(null)
  const [subState, setSubState] = useState(null)

  useEffect(() => {
    visionAPI.getMyJury().then(r => setMyJury(r.data)).catch(() => {})
    visionAPI.getMyVoteState().then(r => setVoteState(r.data)).catch(() => {})
    visionAPI.getMySubmission().then(r => setSubState(r.data)).catch(() => {})
  }, [])

  const show = subState?.show || voteState?.show || myJury?.show
  const _voteMode = voteState?.show?.public_scoring_mode
  const _voteEnabled = voteState?.show?.public_scoring_enabled !== false
  const publicCta = voteState?.show && _voteMode === 'vote' && _voteEnabled && (voteState.is_open || voteState.already_voted)
  const eyebrow = show ? `Show · ${show.name}${show.edition ? ` — ${show.edition}` : ''}` : 'Show'

  /* Live-strip: alleen tonen als er iets actief is (jury open, publiek open, of show fase live) */
  const liveMsg = (() => {
    if (voteState?.is_open) return 'Publiek stemmen is OPEN'
    if (myJury?.jury && !myJury.jury.is_locked) return 'Jury-input open'
    if (show?.phase === 'live') return 'Show is bezig'
    return null
  })()

  const mineLabel = subState?.entry
    ? (subState.entry.team_name ? `Team "${subState.entry.team_name}" ingeschreven` : 'Ingeschreven')
    : (show?.phase === 'setup' ? 'Nog niet ingeschreven' : null)

  return (
    <div className="space-y-6">
      <KempenvisionNav />
      <CompoStatusStrip
        phase={show?.phase}
        phaseLabel={show?.phase ? (KV_FASE[show.phase] || show.phase) : null}
        mineLabel={mineLabel}
        nextAction={kvNextAction({ show, subState, voteState, juryState: myJury })}
        closesAt={show?.submissions_close_at || show?.phase_ends_at || null}
        accent="#C77BFF"
      />
      <PageHeader
        icon={Radio}
        eyebrow={eyebrow}
        title="Kempenvision"
        subtitle="Songfestival-show voor de LAN meeting — AI visuals, jury en publiek stemmen."
        actions={isAdmin && (
          <Button asChild>
            <a href="/kempenvision/stage" target="_blank" rel="noopener">
              <Monitor className="h-4 w-4 mr-2" />Open stage
            </a>
          </Button>
        )}
      />

      {liveMsg && (
        <div className="rounded-md border border-border bg-card px-4 py-3 flex items-center gap-4 flex-wrap"
             style={{ borderLeft: '2px solid rgb(var(--acid-rgb))' }}>
          <span className="live-dot">Nu</span>
          <span className="flex-1 text-sm text-foreground">{liveMsg}</span>
        </div>
      )}

      <RulesCard rules={subState?.show?.rules_text} />

      {/* Jouw inzending */}
      {subState?.show && subState.submission && (
        <SubmissionStatusCard
          submission={subState.submission}
          actionTo="/kempenvision/inleveren"
          subtitleFn={sub => `${sub.team_name ?? sub.proposed_team_name ?? ''}${sub.genre ? ` · ${sub.genre.toUpperCase()}` : ''}`}
        />
      )}

      {/* CTA: inschrijven */}
      {subState?.show?.submissions_open && !subState.submission && (
        <CtaRow
          icon={Music2}
          eyebrow="Doe je mee?"
          title="Lever je nummer in"
          body="Kies of maak een team en upload je AI-track. Admin keurt de inzending goed voor de show."
          cta={{ to: '/kempenvision/inleveren', label: 'Inschrijven' }}
          tone="acid"
        />
      )}

      {/* CTA: publiek stem open */}
      {publicCta && voteState.is_open && !voteState.already_voted && (
        <CtaRow
          icon={Users}
          eyebrow="Publiek stem is open"
          title="Verdeel je punten nu"
          body={`${voteState.show.name}${voteState.show.edition ? ` — ${voteState.show.edition}` : ''} · sluit zodra alle jury's klaar zijn`}
          cta={{ to: '/kempenvision/vote', label: 'Ga stemmen' }}
          tone="live"
        />
      )}

      {/* Bevestiging na stemmen */}
      {publicCta && voteState.already_voted && (
        <Card className="border-border" style={{ borderLeft: '2px solid rgb(var(--live-rgb))' }}>
          <CardContent className="p-4 flex items-center gap-3">
            <PartyPopper className="h-6 w-6 text-live shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-live">Bedankt — je publiek stem is binnen</div>
              <div className="text-sm text-muted-foreground">Volg de uitslag straks live op de beamer.</div>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/kempenvision/vote">Bekijk stem</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Jury status */}
      {myJury?.jury && (
        <Card className="border-border" style={{ borderLeft: '2px solid rgb(var(--gold-rgb))' }}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-11 h-11 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
              <Award className="h-5 w-5 text-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[11px] uppercase tracking-widest text-gold">Je bent jury</div>
              <div className="font-semibold truncate">{myJury.jury.name}</div>
              <div className="text-sm text-muted-foreground truncate">
                {myJury.show?.name}{myJury.show?.edition ? ` — ${myJury.show.edition}` : ''}
                {myJury.jury.is_locked && ' · ingediend'}
              </div>
            </div>
            {myJury.jury.is_locked ? (
              <Badge className="border-live/40 text-live bg-live/5" variant="outline">
                <Lock className="h-3 w-3 mr-1" /> Klaar
              </Badge>
            ) : (
              <Button asChild>
                <Link to="/kempenvision/jury">Ga stemmen <ArrowRight className="h-4 w-4 ml-1" /></Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

/* Herbruikbare CTA-rij in kabinet-stijl.
   tone bepaalt de border-left kleur (acid | live | peach | gold). */
function CtaRow({ icon: Icon, eyebrow, title, body, cta, tone = 'acid' }) {
  const toneVar = {
    acid:  'rgb(var(--acid-rgb))',
    live:  'rgb(var(--live-rgb))',
    peach: 'rgb(var(--peach-rgb))',
    gold:  'rgb(var(--gold-rgb))',
  }[tone]
  const toneText = {
    acid: 'text-acid', live: 'text-live', peach: 'text-peach', gold: 'text-gold',
  }[tone]
  return (
    <Card className="border-border" style={{ borderLeft: `2px solid ${toneVar}` }}>
      <CardContent className="p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-[4px] border border-border bg-card flex items-center justify-center shrink-0">
          <Icon className={`h-5 w-5 ${toneText}`} />
        </div>
        <div className="flex-1 min-w-0">
          {eyebrow && (
            <div className={`font-mono text-[11px] uppercase tracking-widest ${toneText}`}>{eyebrow}</div>
          )}
          <div className="text-lg font-semibold tracking-tight">{title}</div>
          <div className="text-sm text-muted-foreground">{body}</div>
        </div>
        <Button asChild size="lg">
          <Link to={cta.to}>{cta.label} <ArrowRight className="h-4 w-4 ml-1" /></Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function RulesCard({ rules }) {
  if (!rules) return null
  return (
    <div className="rounded-md border border-border bg-card p-4"
         style={{ borderLeft: '2px solid rgb(var(--peach-rgb))' }}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-peach mb-2">Regels & uitleg</div>
      <div className="text-sm whitespace-pre-wrap leading-relaxed">{rules}</div>
    </div>
  )
}